import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

interface LayoutState {
    leftSidebarCollapsed: boolean;
    mobileMenuOpen: boolean;
}

interface LayoutContextType {
    state: LayoutState;
    toggleLeftSidebar: () => void;
    setLeftSidebarCollapsed: (collapsed: boolean) => void;
    toggleMobileMenu: () => void;
    setMobileMenuOpen: (open: boolean) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

interface LayoutProviderProps {
    children: ReactNode;
}

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
    const [state, setState] = useState<LayoutState>({
        leftSidebarCollapsed: false,
        mobileMenuOpen: false,
    });

    const toggleLeftSidebar = () => {
        setState(prev => ({
            ...prev,
            leftSidebarCollapsed: !prev.leftSidebarCollapsed,
        }));
    };

    const setLeftSidebarCollapsed = (collapsed: boolean) => {
        setState(prev => ({
            ...prev,
            leftSidebarCollapsed: collapsed,
        }));
    };

    const toggleMobileMenu = () => {
        setState(prev => ({
            ...prev,
            mobileMenuOpen: !prev.mobileMenuOpen,
        }));
    };

    const setMobileMenuOpen = (open: boolean) => {
        setState(prev => ({
            ...prev,
            mobileMenuOpen: open,
        }));
    };

    return (
        <LayoutContext.Provider value={{
            state,
            toggleLeftSidebar,
            setLeftSidebarCollapsed,
            toggleMobileMenu,
            setMobileMenuOpen,
        }}>
            {children}
        </LayoutContext.Provider>
    );
};

export const useLayout = (): LayoutContextType => {
    const context = useContext(LayoutContext);
    if (!context) {
        throw new Error('useLayout must be used within a LayoutProvider');
    }
    return context;
};
